import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CommentsSection } from '../components/CommentsSection';
import { formatDateShort as formatDate } from '../utils/dateUtils';
import DOMPurify from 'dompurify';
import {
  getInvoice, submitInvoice, markInvoicePaid, deleteInvoice,
  createInvoiceDescription, updateInvoiceDescription, deleteInvoiceDescription,
  mergeInvoices, getInvoiceEmailPreview, sendInvoiceEmail,
  downloadInvoicePDF, getJob, getQuote
} from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, X, Merge, Mail, Send, Download } from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { EmailAttachmentGallery } from '../components/EmailAttachmentGallery';
import { usePageTitle } from '../hooks/usePageTitle';

const statusConfig = {
  0: { label: 'For Submission', bgClass: 'bg-slate-200 text-slate-700' },
  1: { label: 'Unpaid', bgClass: 'bg-yellow-100 text-yellow-800' },
  2: { label: 'Paid', bgClass: 'bg-green-100 text-green-800' },
};

/* ============================================================
   EDIT / ADD DESCRIPTION LINE MODALS
   ============================================================ */
function EditLineModal({ isOpen, onClose, line, onSave, loading }) {
  const [desc, setDesc] = useState(line?.description || '');
  // Round to 2 decimals on init so floating-point junk like 3068.8500000000004
  // doesn't show up in the input.
  const [amount, setAmount] = useState(() =>
    line?.amount != null ? Math.round(Number(line.amount) * 100) / 100 : 0
  );
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[10px] w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center shrink-0">
          <h3 className="font-semibold">Edit Description Line</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="text-sm text-slate-500 block mb-1">Description</label>
            <div className="max-h-[50vh] overflow-y-auto border rounded">
              <RichTextEditor value={desc} onChange={setDesc} placeholder="Enter description..." />
            </div>
          </div>
          <div className="w-1/2">
            <label className="text-sm text-slate-500 block mb-1">Amount</label>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)} className="w-full border rounded-none px-3 py-2 text-sm text-right" data-testid="edit-line-amount" />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 border rounded-sm text-sm">Cancel</button>
          <button onClick={() => onSave({ id: line?.id, description: desc, amount: Math.round(Number(amount) * 100) / 100 })} disabled={loading} className="px-4 py-2 bg-primary text-white rounded-sm text-sm flex items-center gap-2" data-testid="save-line-btn">
            {loading && <Loader2 size={14} className="animate-spin" />}Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function AddLineModal({ isOpen, onClose, onAdd, loading }) {
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState(0);
  React.useEffect(() => { if (isOpen) { setDesc(''); setAmount(0); } }, [isOpen]);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[10px] w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b shrink-0"><h3 className="font-semibold">Add New Line</h3></div>
        <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="text-sm text-slate-500 block mb-1">Description</label>
            <div className="max-h-[50vh] overflow-y-auto border rounded">
              <RichTextEditor value={desc} onChange={setDesc} placeholder="Enter description..." />
            </div>
          </div>
          <div className="w-1/2">
            <label className="text-sm text-slate-500 block mb-1">Amount</label>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)} className="w-full border rounded-none px-3 py-2 text-sm text-right" data-testid="new-line-amount" />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 border rounded-sm text-sm">Cancel</button>
          <button onClick={() => { if (!desc && !amount) { toast.error('Please fill all fields'); return; } onAdd({ description: desc, amount: Math.round(Number(amount) * 100) / 100 }); }} disabled={loading} className="px-4 py-2 bg-primary text-white rounded-sm text-sm flex items-center gap-2" data-testid="add-line-btn">
            {loading && <Loader2 size={14} className="animate-spin" />}Add
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ isOpen, title, message, onClose, onConfirm, loading, confirmLabel, confirmColor }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[10px] w-full max-w-sm">
        <div className="p-4 border-b"><h3 className="font-semibold">{title}</h3></div>
        <div className="p-6 text-center">
          <p className="text-sm text-slate-500">{message}</p>
          <p className="text-sm mt-1">Click confirm to continue</p>
        </div>
        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-sm text-sm" data-testid="confirm-modal-cancel">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`px-4 py-2 text-white rounded-sm text-sm flex items-center gap-2 ${confirmColor || 'bg-primary'}`} data-testid="confirm-modal-ok">
            {loading && <Loader2 size={14} className="animate-spin" />}{confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN INVOICE DETAIL PAGE
   ============================================================ */
function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editLine, setEditLine] = useState(null);
  const [showAddLine, setShowAddLine] = useState(false);
  const [removeLine, setRemoveLine] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const { data: invoice, isLoading, refetch } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoice(id).then(res => res.data)
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['invoice', id] });

  usePageTitle(invoice?.invoice_number);

  const cityRegionPostalLine = useMemo(
    () => [
      invoice?.po_city || invoice?.client?.po_city,
      invoice?.po_region || invoice?.client?.po_region,
      invoice?.po_postal_code || invoice?.client?.po_postal_code,
    ].filter(Boolean).join(' '),
    [invoice]
  );

  const submitMutation = useMutation({
    mutationFn: () => submitInvoice(id),
    onSuccess: () => { invalidate(); setShowSubmit(false); toast.success('Invoice submitted'); }
  });

  const markPaidMutation = useMutation({
    mutationFn: () => markInvoicePaid(id),
    onSuccess: () => { invalidate(); setShowMarkPaid(false); toast.success('Invoice marked as paid'); }
  });

  const addLineMutation = useMutation({
    mutationFn: (data) => createInvoiceDescription({ invoice_id: id, ...data }),
    onSuccess: () => { invalidate(); setShowAddLine(false); toast.success('Line added'); }
  });

  const editLineMutation = useMutation({
    mutationFn: ({ lineId, ...data }) => updateInvoiceDescription(lineId, data),
    onSuccess: () => { invalidate(); setEditLine(null); toast.success('Line updated'); }
  });

  const removeLineMutation = useMutation({
    mutationFn: (lineId) => deleteInvoiceDescription(lineId),
    onSuccess: () => { invalidate(); setRemoveLine(null); toast.success('Line removed'); }
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data) => sendInvoiceEmail(id, data),
    onSuccess: (res) => {
      invalidate(); setShowEmailModal(false);
      if (res?.data?.pending) toast.info(res.data.message, { duration: 10000 });
      else toast.success('Invoice email sent successfully');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to send email')
  });

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 size={32} className="animate-spin text-primary" /></div>;
  if (!invoice) return <div className="text-center py-12 text-slate-500">Invoice not found</div>;

  const st = statusConfig[invoice.status] || statusConfig[0];
  const descriptions = invoice.descriptions || [];
  const subtotal = descriptions.reduce((sum, d) => sum + (d.amount || 0), 0);
  const gst = subtotal * 0.10;
  const amountDue = subtotal + gst;

  // formatDate imported from utils/dateUtils

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="invoice-detail-page">
      {/* Top bar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/invoices" className="text-slate-500 hover:text-slate-800" data-testid="back-to-invoices"><ArrowLeft size={20} /></Link>
          <span className="text-slate-500">Invoice No:</span>
          <span className="text-2xl font-bold text-primary" data-testid="invoice-number">{invoice.invoice_number}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={async () => {
            try {
              toast.info('Generating PDF...');
              const res = await downloadInvoicePDF(invoice.id);
              const blob = new Blob([res.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.style.display = 'none';
              a.href = url;
              a.download = `Invoice_${invoice.invoice_number || invoice.id}.pdf`;
              document.body.appendChild(a);
              a.click();
              setTimeout(() => { window.URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
              toast.success('PDF downloaded');
            } catch { toast.error('Failed to generate PDF'); }
          }} className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-sm hover:bg-red-600 flex items-center gap-1" data-testid="download-invoice-pdf-btn">
            <Download size={14} /> Download as PDF
          </button>
          <button onClick={() => setShowEmailModal(true)} className="px-3 py-1.5 text-sm bg-primary text-white rounded-sm hover:bg-primary/90 flex items-center gap-1" data-testid="email-invoice-btn">
            <Mail size={14} /> Email
          </button>
          {invoice.status === 0 && (
            <button onClick={() => setShowSubmit(true)} className="px-3 py-1.5 text-sm bg-green-500 text-white rounded-sm hover:bg-green-600" data-testid="submit-manually-btn">
              Submit Manually
            </button>
          )}
          {invoice.status < 2 && (
            <button onClick={() => setShowMarkPaid(true)} className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded-sm hover:bg-yellow-600" data-testid="mark-paid-btn">
              Mark as Paid
            </button>
          )}
        </div>
      </div>

      {/* Merge Banner — only for non-deposit invoices with other job invoices on same quote */}
      {!invoice.is_deposit && invoice.mergeable_invoices?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="merge-invoices-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Merge size={16} className="text-amber-700" />
              <span className="font-semibold text-amber-800 text-sm">
                Same Quote ({invoice.quote_number}) — Job Invoice{invoice.mergeable_invoices.length > 1 ? 's' : ''} to merge:
              </span>
              {invoice.mergeable_invoices.map(mi => (
                <Link key={mi.id} to={`/invoices/${mi.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200 text-amber-900 text-sm rounded hover:bg-amber-300">
                  {mi.invoice_number} — ${mi.subtotal?.toFixed(2)}
                </Link>
              ))}
            </div>
            <button
              onClick={async () => {
                if (!window.confirm(`Merge ${invoice.mergeable_invoices.length} job invoice(s) into ${invoice.invoice_number}? The deposit invoice will remain untouched as a separate record.`)) return;
                try {
                  await mergeInvoices({
                    target_invoice_id: invoice.id,
                    source_invoice_ids: invoice.mergeable_invoices.map(m => m.id)
                  });
                  toast.success('Job invoices merged successfully');
                  refetch();
                } catch { toast.error('Merge failed'); }
              }}
              className="px-4 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center gap-2 whitespace-nowrap"
              data-testid="merge-invoices-btn"
            >
              <Merge size={14} /> Merge into this Invoice
            </button>
          </div>
        </div>
      )}

      {/* Invoice Card */}
      <div className="bg-white rounded-[10px] border overflow-hidden" id="printable-invoice">
        <div className="p-8 overflow-x-hidden" style={{ minHeight: '60vh', position: 'relative' }}>
          {/* Status + Logo row */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-2">
              <div className={`px-4 py-1.5 rounded text-sm font-semibold ${st.bgClass}`} data-testid="invoice-status-badge">
                {st.label}
              </div>
              {invoice.is_exported && (
                <div className="px-3 py-1.5 rounded text-sm font-semibold bg-blue-100 text-blue-700" data-testid="invoice-exported-badge">
                  Exported
                </div>
              )}
            </div>
            <img src="/logo.png" alt="PMP Logo" className="h-14 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>

          {/* Header: Bill To / Invoice Info / Company Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Bill To */}
            <div>
              <h1 className="text-3xl font-bold mb-3">{invoice.is_deposit ? 'DEPOSIT INVOICE' : 'TAX INVOICE'}</h1>
              <p className="text-sm font-semibold">{invoice.contact_name}</p>
              <p className="text-sm text-slate-600">
                {invoice.po_address_line1 || invoice.client?.po_address_line1}
              </p>
              {(invoice.po_address_line2 || invoice.client?.po_address_line2) && (
                <p className="text-sm text-slate-600">
                  {invoice.po_address_line2 || invoice.client?.po_address_line2}
                </p>
              )}
              <p className="text-sm text-slate-600">
                {cityRegionPostalLine}
              </p>
              <p className="text-sm text-slate-600">{invoice.po_country || invoice.client?.po_country}</p>
            </div>

            {/* Invoice Meta */}
            <div className="space-y-3">
              <div>
                <p className="text-sm font-bold">Invoice Date</p>
                <p className="text-sm text-slate-500">{formatDate(invoice.invoice_date)}</p>
              </div>
              <div>
                <p className="text-sm font-bold">Invoice Number</p>
                <p className="text-sm text-slate-500">{invoice.invoice_number}</p>
              </div>
              <div>
                <p className="text-sm font-bold">Reference</p>
                <p className="text-sm text-slate-500">{invoice.reference || '-'}</p>
              </div>
              {invoice.quote_number && (
                <div>
                  <p className="text-sm font-bold">Quote</p>
                  <Link to={`/quotes/${invoice.quote_id}`} className="text-sm text-primary hover:underline">{invoice.quote_number}</Link>
                </div>
              )}
              {invoice.job_number && (
                <div>
                  <p className="text-sm font-bold">Job</p>
                  <Link to={`/jobs/${invoice.job_id}`} className="text-sm text-primary hover:underline">{invoice.job_number}</Link>
                </div>
              )}
            </div>

            {/* Company Info */}
            <div className="text-right text-sm space-y-1">
              <p>Phone: 08 6204 8116</p>
              <p className="mb-3">Mobile: 0412 627 697</p>
              <p>ABN: 69 516 939 208</p>
              <p className="text-primary mb-3">info@projectmaintenanceperth.com</p>
              <p className="font-bold">Registered Painting Contractor: 100457</p>
            </div>
          </div>

          {/* Description Table */}
          <table className="w-full mb-2 table-fixed" data-testid="invoice-descriptions-table">
            <thead>
              <tr className="border-b-2 border-slate-800">
                <th className="text-left py-2 text-sm font-bold">Description</th>
                <th className="text-right py-2 text-sm font-bold" style={{ width: '10%' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {descriptions.map((desc, idx) => (
                <tr key={desc.id} className="border-b" data-testid={`invoice-desc-row-${idx}`}>
                  <td className="py-3 pr-4 text-sm overflow-hidden">
                    <div className="prose prose-sm max-w-none break-words overflow-hidden" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(desc.description || '') }} />
                    <div className="mt-2 flex gap-2 print:hidden">
                      <button
                        onClick={() => setEditLine(desc)}
                        className="px-3 py-1 text-xs bg-yellow-400 hover:bg-yellow-500 text-slate-800 rounded"
                        data-testid={`edit-desc-${idx}`}
                      >
                        Edit this line
                      </button>
                      <button
                        onClick={() => setRemoveLine(desc)}
                        className="px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded"
                        data-testid={`remove-desc-${idx}`}
                      >
                        Remove this line
                      </button>
                    </div>
                  </td>
                  <td className="py-3 text-right text-sm align-top">{(desc.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
              {descriptions.length === 0 && (
                <tr><td colSpan={2} className="py-6 text-center text-sm text-slate-400">No description lines yet</td></tr>
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div className="mb-4">
            <table className="w-full">
              <tbody>
                <tr className="border-t-2 text-sm font-bold">
                  <td></td>
                  <td className="text-right py-2">Subtotal</td>
                  <td className="text-right py-2" style={{ width: '125px' }}>{subtotal.toFixed(2)}</td>
                </tr>
                <tr className="text-sm font-bold">
                  <td></td>
                  <td className="text-right py-2">Total GST 10%</td>
                  <td className="text-right py-2" style={{ width: '125px' }}>{gst.toFixed(2)}</td>
                </tr>
                <tr className="text-sm font-bold">
                  <td></td>
                  <td className="text-right py-2">Amount Due AUD</td>
                  <td className="text-right py-2" style={{ width: '125px' }}>{amountDue.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Add New Line button */}
          <div className="flex justify-center mb-8 print:hidden px-16">
            <button
              onClick={() => setShowAddLine(true)}
              className="w-full py-2.5 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600 flex items-center justify-center gap-2 font-medium"
              data-testid="add-new-line-btn"
            >
              <Plus size={14} /> Add New Line
            </button>
          </div>

          {/* Footer / Payment Terms */}
          <div className="text-[10px] text-slate-500 space-y-0.5 mt-12 border-t pt-4 relative">
            <p className="font-bold">Payment due on receipt of invoice</p>
            <p className="font-bold">Credit Card payment will incur an additional 2.5% bank fee to invoice amount due</p>
            <p className="font-bold">Project Maintenance & Painters Perth reserves the right to charge interest on any payments not made as above at the rate of 2% per month</p>
            <p className="font-bold">Project Maintenance & Painters Perth may at any time require security for payment and may withhold delivery until provision of sufficient security or Project Maintenance & Painters Perth may at its option, suspend or terminate the contract and the payment of all materials delivered and work commenced up to the date of such suspension or termination and all moneys payable here under shall be immediately become due and payable</p>
            <p className="font-bold">All costs and expenses incurred by Project Maintenance & Painters Perth in enforcing these conditions of sale including all legal costs incurred in recovering any debts owed to Project Maintenance & Painters Perth by the Customer, shall be payable by the Customer.</p>
            <div className="mt-2">
              <p className="font-bold">Electronic Bank Payment Details:</p>
              <p className="font-bold">BSB - 016281 Account Number - 289 864 693</p>
            </div>
            <img src="/eco-friendly.png" alt="Eco Friendly" className="absolute bottom-0 right-[10%] w-20 hidden print:block" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        </div>
      </div>

      {/* Comments */}
      <div className="mt-6 print:hidden">
        <CommentsSection entityType="invoice" entityId={id} />
      </div>

      {/* Modals */}
      <EditLineModal
        key={editLine?.id || 'new'}
        isOpen={!!editLine}
        onClose={() => setEditLine(null)}
        line={editLine}
        onSave={({ id: lineId, description, amount }) => editLineMutation.mutate({ lineId, description, amount })}
        loading={editLineMutation.isPending}
      />
      <AddLineModal
        isOpen={showAddLine}
        onClose={() => setShowAddLine(false)}
        onAdd={(data) => addLineMutation.mutate(data)}
        loading={addLineMutation.isPending}
      />
      <ConfirmModal
        isOpen={!!removeLine}
        title="Remove line confirmation"
        message="Are you sure you want to remove this line?"
        onClose={() => setRemoveLine(null)}
        onConfirm={() => removeLineMutation.mutate(removeLine.id)}
        loading={removeLineMutation.isPending}
        confirmLabel="Confirm"
        confirmColor="bg-red-500"
      />
      <ConfirmModal
        isOpen={showSubmit}
        title="Submit Invoice"
        message="Are you sure you want to submit this INVOICE manually?"
        onClose={() => setShowSubmit(false)}
        onConfirm={() => submitMutation.mutate()}
        loading={submitMutation.isPending}
        confirmLabel="Confirm"
        confirmColor="bg-green-500"
      />
      <ConfirmModal
        isOpen={showMarkPaid}
        title="Mark as Paid"
        message="Are you sure you want to mark this Invoice as PAID?"
        onClose={() => setShowMarkPaid(false)}
        onConfirm={() => markPaidMutation.mutate()}
        loading={markPaidMutation.isPending}
        confirmLabel="Confirm"
        confirmColor="bg-yellow-500"
      />
      {showEmailModal && <InvoiceEmailModal invoiceId={id} invoice={invoice} onClose={() => setShowEmailModal(false)} onSend={(data) => sendEmailMutation.mutate(data)} loading={sendEmailMutation.isPending} />}
    </div>
  );
}

/* ============================================================
   INVOICE EMAIL MODAL
   ============================================================ */
function InvoiceEmailModal({ invoiceId, invoice, onClose, onSend, loading }) {
  const [form, setForm] = useState({ recipient1: '', recipient2: '', recipient3: '', subject: '', message: '', include_attachments: true });
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState(new Set());

  useEffect(() => {
    getInvoiceEmailPreview(invoiceId).then(res => {
      setForm(prev => ({ ...prev, ...res.data }));
    }).catch(() => {
      setForm(prev => ({ ...prev, subject: `Invoice ${invoice?.invoice_number || ''}` }));
    }).finally(() => setLoadingPreview(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  // Photos & files come from the linked Job (preferred). For deposit-only invoices
  // that aren't linked to a job yet, fall back to the linked Quote.
  const jobId = invoice?.job_id || null;
  const quoteId = invoice?.quote_id || null;
  const useJobSource = !!jobId;

  const { data: parentEntity } = useQuery({
    queryKey: useJobSource ? ['job-for-invoice-email', jobId] : ['quote-for-invoice-email', quoteId],
    queryFn: () =>
      (useJobSource ? getJob(jobId) : getQuote(quoteId)).then(r => r.data),
    enabled: !!(useJobSource ? jobId : quoteId),
  });

  const sources = useMemo(() => {
    if (!parentEntity) return [];
    const descs = parentEntity.descriptions || [];
    if (useJobSource) {
      return [
        { entity_type: 'job', entity_id: jobId },
        ...descs.map(d => ({ entity_type: 'job_description', entity_id: d.id })),
      ];
    }
    return [
      { entity_type: 'quote', entity_id: quoteId },
      ...descs.map(d => ({ entity_type: 'quote_description', entity_id: d.id })),
    ];
  }, [parentEntity, useJobSource, jobId, quoteId]);

  const uploadTo = useJobSource
    ? { entity_type: 'job', entity_id: jobId }
    : (quoteId ? { entity_type: 'quote', entity_id: quoteId } : null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.recipient1) { toast.error('Please enter at least one recipient'); return; }
    onSend({ ...form, included_photo_ids: Array.from(selectedAttachmentIds) });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-slate-500">Invoice &gt; {invoice?.invoice_number} &gt; Send Email</div>
            <h2 className="font-semibold">Email Invoice</h2>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {loadingPreview ? (
          <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-primary" /></div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium mb-1">Recipient 1 *</label><input type="text" value={form.recipient1} onChange={e => setForm({...form, recipient1: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="inv-email-recipient1" /></div>
            <div><label className="block text-xs font-medium mb-1">Recipient 2</label><input type="text" value={form.recipient2} onChange={e => setForm({...form, recipient2: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="inv-email-recipient2" /></div>
            <div><label className="block text-xs font-medium mb-1">Recipient 3</label><input type="text" value={form.recipient3} onChange={e => setForm({...form, recipient3: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="inv-email-recipient3" /></div>
          </div>
          <div><label className="block text-xs font-medium mb-1">Subject</label><input type="text" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="inv-email-subject" /></div>
          <div><label className="block text-xs font-medium mb-1">Message</label><RichTextEditor value={form.message} onChange={val => setForm({...form, message: val})} /></div>

          {/* PDF toggle */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium">PDF:</label>
            <input type="checkbox" checked={form.include_attachments} onChange={e => setForm({...form, include_attachments: e.target.checked})} className="w-4 h-4" data-testid="inv-email-attachments" />
            {form.include_attachments && (
              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1 rounded text-sm"><Download size={14} className="text-red-500" /> Invoice_{invoice?.invoice_number}.pdf</div>
            )}
          </div>

          {/* Photos & files gallery (from linked Job, or Quote for deposit-only invoices) */}
          {uploadTo && (
            <EmailAttachmentGallery
              sources={sources}
              uploadTo={uploadTo}
              selectedIds={selectedAttachmentIds}
              onSelectionChange={setSelectedAttachmentIds}
              title={useJobSource ? 'Photos & Files (from Job)' : 'Photos & Files (from Quote)'}
              emptyHint="No attachments yet."
            />
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-primary text-white rounded flex items-center gap-2" data-testid="send-inv-email-btn">
              {loading && <Loader2 size={16} className="animate-spin" />} <Send size={16} /> Send
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export default InvoiceDetail;
